import {Component, computed, effect, inject, signal, viewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormControl} from '@angular/forms';
import {map} from 'rxjs';
import {TranslatePipe} from '@ngx-translate/core';
import {CustomerProductSummary} from '../../../../../api';
import {ElciaCard} from '../../../../components/card/card';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {Search} from '../../../../components/search/search';
import {ElciaDialog} from '../../../../components/dialog/dialog';
import {ConfirmDialog} from '../../../../components/confirm-dialog/confirm-dialog';
import {ConfirmService} from '../../../../services/confirm.service';
import {CustomerProductQueryService} from './services/customer-product-query.service';
import {CustomerProductCommandService} from './services/customer-product-command.service';
import {CustomerProductForm, CustomerProductFormValue} from './components/customer-product-form/customer-product-form';
import {CustomerProductItem} from './components/customer-product-item/customer-product-item';
import {CustomerProductHistory} from './components/customer-product-history/customer-product-history';
import {productMatchesSearch} from './customer-product.utils';

@Component({
  selector: 'elcia-customer-products',
  imports: [
    TranslatePipe,
    ElciaCard,
    ElciaButton,
    ElciaDialog,
    ConfirmDialog,
    CustomerProductForm,
    CustomerProductItem,
    CustomerProductHistory,
    Search,
  ],
  providers: [CustomerProductQueryService, CustomerProductCommandService],
  templateUrl: './customer-products.html',
  styleUrl: './customer-products.scss',
  host: {class: 'flex flex-col gap-4 min-h-0 overflow-y-auto soft-scrollbar'},
})
export class CustomerProducts {
  private readonly route = inject(ActivatedRoute);
  private readonly queryService = inject(CustomerProductQueryService);
  private readonly commandService = inject(CustomerProductCommandService);
  private readonly confirmService = inject(ConfirmService);

  private readonly productForm = viewChild<CustomerProductForm>(CustomerProductForm);

  private readonly customerId = toSignal(this.route.parent!.params.pipe(map(params => Number(params['id']))));

  private readonly syncCustomerId = effect(() => {
    this.queryService.customerId.set(this.customerId() ?? null);
  });

  protected readonly searchControl = new FormControl<string>('', {nonNullable: true});
  private readonly searchTerm = toSignal(this.searchControl.valueChanges, {initialValue: ''});

  protected readonly isLoading = computed(() => this.queryService.isLoading());
  protected readonly recurringProducts = computed(() =>
    this.queryService.recurringProducts().filter(product => productMatchesSearch(product, this.searchTerm())));
  protected readonly oneShotProducts = computed(() =>
    this.queryService.oneShotProducts().filter(product => productMatchesSearch(product, this.searchTerm())));

  protected readonly history = computed(() => this.queryService.history());
  protected readonly isHistoryLoading = computed(() => this.queryService.isHistoryLoading());

  // Set by a timeline click, replaced by the next one: no timer. The highlighted row scrolls itself into
  // view, so a licence sitting far down the list is never highlighted off screen.
  protected readonly highlightedProductId = signal<number | null>(null);

  // Clicking the same entry again writes the same id, which notifies nobody. The tick is what makes the
  // row scroll back into view after the user has scrolled away.
  protected readonly highlightTick = signal(0);

  protected readonly isFormDisplayed = signal(false);
  protected readonly openMode = signal<'create' | 'edit'>('create');
  protected readonly selectedProduct = signal<CustomerProductSummary | null>(null);

  protected readonly isSaving = computed(() =>
    this.commandService.createMutation.isPending() || this.commandService.updateMutation.isPending()
  );

  protected highlightProduct(customerProductId: number): void {
    this.highlightedProductId.set(customerProductId);
    this.highlightTick.update(tick => tick + 1);
  }

  protected showForm(): void {
    this.selectedProduct.set(null);
    this.openMode.set('create');
    this.isFormDisplayed.set(true);
  }

  protected openEditForm(product: CustomerProductSummary): void {
    this.selectedProduct.set(product);
    this.openMode.set('edit');
    this.isFormDisplayed.set(true);
  }

  protected closeForm(): void {
    this.isFormDisplayed.set(false);
    this.selectedProduct.set(null);
  }

  protected onDialogSave(): void {
    this.productForm()?.submit();
  }

  protected onFormSubmit(value: CustomerProductFormValue): void {
    if (this.openMode() === 'edit') {
      const id = this.selectedProduct()?.id;
      if (id == null) return;
      this.commandService.updateMutation.mutate({
        id,
        body: {
          productId: value.productId,
          startDate: value.startDate,
          endDate: value.endDate,
          quantity: value.quantity,
          price: value.price,
          mustIncludeInInvoicing: value.mustIncludeInInvoicing,
          description: value.description,
          isActive: value.isActive,
          features: value.features,
        },
      });
    } else {
      const customerId = this.customerId();
      if (customerId == null) return;
      this.commandService.createMutation.mutate({
        customerId,
        productId: value.productId,
        startDate: value.startDate,
        endDate: value.endDate,
        quantity: value.quantity,
        price: value.price,
        mustIncludeInInvoicing: value.mustIncludeInInvoicing,
        description: value.description,
        features: value.features,
      });
    }
  }

  protected deleteProduct(product: CustomerProductSummary): void {
    this.confirmService.confirm({
      title: 'entity.product',
      message: product.productName,
      accept: () => {
        this.commandService.deleteMutation.mutate(product.id);
      },
    });
  }

  private readonly closeFormOnSuccess = effect(() => {
    const createSuccess = this.commandService.createMutation.isSuccess();
    const updateSuccess = this.commandService.updateMutation.isSuccess();
    if (createSuccess || updateSuccess) {
      this.closeForm();
    }
  });
}
